import { createRootRoute, createRoute, createRouter, redirect } from '@tanstack/react-router';
import type { AssetKind } from '@shared/types';
import { t } from '@/lib/i18n';
import { AppShell } from './pages/AppShell';
import { LoginPage } from './pages/LoginPage';
import { MagicPage } from './pages/MagicPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { BrowsePage } from './pages/BrowsePage';
import { HomePage } from './pages/HomePage';
import { AssetDetailPage } from './pages/AssetDetailPage';
import { SearchPage } from './pages/SearchPage';
import { ReviewPage } from './pages/ReviewPage';
import { PromptPage } from './pages/PromptPage';
import { AccountPage } from './pages/AccountPage';
import { UsersPage } from './pages/UsersPage';
import { ActivityPage } from './pages/ActivityPage';
import { ArtworkPage } from './pages/ArtworkPage';
import { SettingsPage } from './pages/SettingsPage';
import { BasketPage } from './pages/BasketPage';
import { RecycleBinPage } from './pages/RecycleBinPage';
import { SharedPage } from './pages/SharedPage';
import { api } from './lib/api';
import { keys } from './lib/queries';
import { queryClient } from './lib/queryClient';

const rootRoute = createRootRoute({
  notFoundComponent: () => (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-2 surface-page text-center">
      <p className="text-3xl font-semibold text-strong">404</p>
      <a href="/" className="text-sm text-brand-500 hover:underline">{t('notFound.backToLibrary')}</a>
    </div>
  ),
});

async function requireAuth() {
  try {
    return { user: await queryClient.ensureQueryData({ queryKey: keys.me, queryFn: () => api.me().then((r) => r.user), staleTime: 15 * 60_000 }) };
  } catch {
    throw redirect({ to: '/login' });
  }
}

// Admin-only guard for the Manage pages.
async function requireAdmin() {
  const { user } = await requireAuth();
  if (!user?.isAdmin) throw redirect({ to: '/' });
}

type BrowseSearch = {
  /** Includes the 'pdf' pseudo-kind — see AssetFilter. */
  kind?: AssetKind | 'pdf' | 'font';
  sort?: string;
  order?: 'asc' | 'desc';
  untagged?: boolean;
  favourites?: boolean;
};
const parseBrowseSearch = (s: Record<string, unknown>): BrowseSearch => ({
  kind: s.kind as AssetKind | 'pdf' | 'font' | undefined,
  sort: typeof s.sort === 'string' ? s.sort : undefined,
  order: s.order === 'desc' ? 'desc' : s.order === 'asc' ? 'asc' : undefined,
  untagged: s.untagged === true || s.untagged === 'true' ? true : undefined,
  favourites: s.favourites === true || s.favourites === 'true' ? true : undefined,
});

/**
 * One param per filter column, mirroring the search grid. `imageType`
 * (singular) is kept because the rail's facet links still use it.
 */
type SearchSearch = {
  q?: string;
  brand?: string;
  productTitle?: string;
  description?: string;
  fileName?: string;
  path?: string;
  hashtag?: string;
  imageType?: string;
  location?: string;
};
const str = (v: unknown) => (typeof v === 'string' && v !== '' ? v : undefined);
const parseSearchSearch = (s: Record<string, unknown>): SearchSearch => ({
  q: str(s.q),
  brand: str(s.brand),
  productTitle: str(s.productTitle),
  description: str(s.description),
  fileName: str(s.fileName),
  path: str(s.path),
  hashtag: str(s.hashtag),
  imageType: str(s.imageType),
  location: str(s.location),
});

/**
 * Artwork filters live in the URL, not component state, so a lookup survives
 * opening one of its files. Someone searches a SAP code, expands the product,
 * clicks the master PDF and comes back — with the filters in state that round
 * trip landed them on an empty index and they had to type the code again.
 * `open` carries the expanded product's pdfKey for the same reason.
 */
type ArtworkSearch = { q?: string; brand?: string; sap?: string; job?: string; open?: string };
const parseArtworkSearch = (s: Record<string, unknown>): ArtworkSearch => ({
  q: str(s.q),
  brand: str(s.brand),
  sap: str(s.sap),
  job: str(s.job),
  open: str(s.open),
});

const loginRoute = createRoute({ getParentRoute: () => rootRoute, path: '/login', component: LoginPage });
const magicRoute = createRoute({ getParentRoute: () => rootRoute, path: '/auth/magic', component: MagicPage });
// Child of the root, not the shell: someone resetting a password by definition
// cannot sign in yet, so requireAuth must not run.
const resetRoute = createRoute({ getParentRoute: () => rootRoute, path: '/auth/reset', component: ResetPasswordPage });

// Recipient view for a share link. A child of the ROOT, not the shell: it must
// render for people with no account, and must not show the library chrome.
const sharedRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/share/$token',
  component: SharedPage,
});

const shellRoute = createRoute({ getParentRoute: () => rootRoute, id: 'shell', beforeLoad: requireAuth, component: AppShell });

// "/" is the folder landing screen — no assets until a folder is picked. The
// library-wide grid and its smart views live at "/all" so they stay linkable.
const indexRoute = createRoute({ getParentRoute: () => shellRoute, path: '/', component: HomePage });
const allAssetsRoute = createRoute({ getParentRoute: () => shellRoute, path: '/all', component: BrowsePage, validateSearch: parseBrowseSearch });
const folderRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/folders/$folderId',
  parseParams: ({ folderId }) => ({ folderId: Number(folderId) }),
  validateSearch: parseBrowseSearch,
  component: BrowsePage,
});
const assetRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/assets/$assetId',
  parseParams: ({ assetId }) => ({ assetId: Number(assetId) }),
  component: AssetDetailPage,
});
const searchRoute = createRoute({ getParentRoute: () => shellRoute, path: '/search', component: SearchPage, validateSearch: parseSearchSearch });
const reviewRoute = createRoute({ getParentRoute: () => shellRoute, path: '/review', beforeLoad: requireAdmin, component: ReviewPage });
const promptRoute = createRoute({ getParentRoute: () => shellRoute, path: '/prompt', beforeLoad: requireAdmin, component: PromptPage });
const usersRoute = createRoute({ getParentRoute: () => shellRoute, path: '/users', beforeLoad: requireAdmin, component: UsersPage });
const activityRoute = createRoute({ getParentRoute: () => shellRoute, path: '/activity', beforeLoad: requireAdmin, component: ActivityPage });
const settingsRoute = createRoute({ getParentRoute: () => shellRoute, path: '/settings', beforeLoad: requireAdmin, component: SettingsPage });
const accountRoute = createRoute({ getParentRoute: () => shellRoute, path: '/account', component: AccountPage });
const basketRoute = createRoute({ getParentRoute: () => shellRoute, path: '/basket', component: BasketPage });
const recycleBinRoute = createRoute({ getParentRoute: () => shellRoute, path: '/recyclebin', component: RecycleBinPage });
// Not admin-gated: artwork lookup is everyday work for the brand teams. Access
// to the underlying files is enforced per-asset by the API, not by the route.
const artworkRoute = createRoute({ getParentRoute: () => shellRoute, path: '/artwork', component: ArtworkPage, validateSearch: parseArtworkSearch });

const routeTree = rootRoute.addChildren([
  loginRoute,
  magicRoute,
  resetRoute,
  sharedRoute,
  shellRoute.addChildren([indexRoute, allAssetsRoute, folderRoute, assetRoute, searchRoute, artworkRoute, reviewRoute, promptRoute, usersRoute, activityRoute, settingsRoute, accountRoute, basketRoute, recycleBinRoute]),
]);

export const router = createRouter({ routeTree, defaultPreload: 'intent' });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
